import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { inr, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Storefront, ShoppingCart, MagnifyingGlass, Plus, Minus, Trash, CurrencyInr,
  Printer, WhatsappLogo, Envelope, CheckCircle, UserCircle, Barcode,
} from "@phosphor-icons/react";

const PAY_METHODS = ["Cash", "UPI", "PhonePe", "Google Pay", "Paytm", "Card", "Net Banking", "NEFT", "RTGS", "IMPS", "Wallet", "Cheque"];

function num(v) { return Number(v || 0); }

export default function POS() {
  const [products, setProducts] = useState([]);
  const [services, setServices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("products"); // products | services
  const [category, setCategory] = useState("All");
  const [cart, setCart] = useState([]); // [{key, kind, ref_id, name, code, category, quantity, rate, discount, gst_percent}]
  const [billType, setBillType] = useState("GST"); // GST | NonGST
  const [billDiscount, setBillDiscount] = useState(0);
  const [notes, setNotes] = useState("");
  const [customer, setCustomer] = useState({ id: "", name: "Walk-in", mobile: "" });
  const [custSearch, setCustSearch] = useState("");
  const [payOpen, setPayOpen] = useState(false);
  const [payments, setPayments] = useState([{ method: "Cash", amount: 0, txn_ref: "" }]);
  const [receipt, setReceipt] = useState(null);
  const [kpi, setKpi] = useState({ bills_today: 0, sales_today: 0, cash_today: 0, upi_today: 0, unpaid_count: 0 });

  const loadAll = useCallback(async () => {
    const [p, s, c, k] = await Promise.all([
      api.get("/pos/products"),
      api.get("/services"),
      api.get("/customers"),
      api.get("/pos/dashboard"),
    ]);
    setProducts(p.data); setServices(s.data); setCustomers(c.data); setKpi(k.data);
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);

  const productCats = useMemo(() => {
    const cats = new Set(products.map((p) => p.category).filter(Boolean));
    return ["All", ...Array.from(cats).sort()];
  }, [products]);

  const catalog = useMemo(() => {
    const src = tab === "products" ? products : services;
    const query = q.trim().toLowerCase();
    return src.filter((it) => {
      if (tab === "products" && category !== "All" && it.category !== category) return false;
      if (!query) return true;
      const bag = [it.name, it.code, it.category, it.department].filter(Boolean).join(" ").toLowerCase();
      return bag.includes(query);
    }).slice(0, 60);
  }, [products, services, q, tab, category]);

  const addToCart = (item) => {
    setCart((prev) => {
      const key = `${tab}:${item.id}`;
      const existing = prev.find((x) => x.key === key);
      if (existing) {
        return prev.map((x) => x.key === key ? { ...x, quantity: num(x.quantity) + 1 } : x);
      }
      const baseGst = tab === "products" ? num(item.gst_percent) : 18;
      return [...prev, {
        key,
        kind: tab === "products" ? "product" : "service",
        ref_id: item.id,
        name: item.name,
        code: item.code || "",
        category: item.category || "",
        quantity: 1,
        rate: tab === "products" ? num(item.rate) : (num(item.default_govt_fee) + num(item.default_service_charge)),
        discount: 0,
        gst_percent: billType === "NonGST" ? 0 : baseGst,
      }];
    });
  };

  const addCustomItem = () => {
    const name = window.prompt("Custom item / service name?");
    if (!name || !name.trim()) return;
    const rateStr = window.prompt("Rate (₹)?", "0");
    const rate = num(rateStr);
    setCart((prev) => [...prev, {
      key: `custom:${Date.now()}`,
      kind: "custom",
      ref_id: "",
      name: name.trim(),
      code: "",
      category: "Custom",
      quantity: 1,
      rate,
      discount: 0,
      gst_percent: billType === "NonGST" ? 0 : 18,
    }]);
  };

  const updateLine = (key, patch) => setCart((prev) => prev.map((x) => x.key === key ? { ...x, ...patch } : x));
  const removeLine = (key) => setCart((prev) => prev.filter((x) => x.key !== key));
  const clearCart = () => { setCart([]); setBillDiscount(0); setNotes(""); setCustomer({ id: "", name: "Walk-in", mobile: "" }); };

  const totals = useMemo(() => {
    let subtotal = 0, gst = 0;
    cart.forEach((it) => {
      const gross = num(it.quantity) * num(it.rate);
      const afterDisc = Math.max(0, gross - num(it.discount));
      subtotal += afterDisc;
      gst += afterDisc * num(it.gst_percent) / 100;
    });
    const total = Math.max(0, subtotal - num(billDiscount) + gst);
    return { subtotal: +subtotal.toFixed(2), gst: +gst.toFixed(2), total: +total.toFixed(2) };
  }, [cart, billDiscount]);

  const filteredCustomers = useMemo(() => {
    const query = custSearch.trim().toLowerCase();
    if (!query) return customers.slice(0, 20);
    return customers.filter((c) =>
      (c.name || "").toLowerCase().includes(query)
      || (c.mobile || "").includes(custSearch)
      || (c.customer_no || "").toLowerCase().includes(query)
    ).slice(0, 20);
  }, [custSearch, customers]);

  const pickCustomer = (c) => {
    setCustomer({ id: c.id, name: c.name, mobile: c.mobile || "" });
    setCustSearch("");
  };

  const openPayDialog = () => {
    if (cart.length === 0) { toast.error("Add at least one item"); return; }
    setPayments([{ method: "Cash", amount: totals.total, txn_ref: "" }]);
    setPayOpen(true);
  };

  const paymentsTotal = useMemo(() => payments.reduce((s, p) => s + num(p.amount), 0), [payments]);
  const paymentsShort = +(totals.total - paymentsTotal).toFixed(2);

  const addSplit = () => setPayments((p) => [...p, { method: "UPI", amount: Math.max(0, paymentsShort), txn_ref: "" }]);
  const updatePayment = (i, patch) => setPayments((prev) => prev.map((p, idx) => idx === i ? { ...p, ...patch } : p));
  const removePayment = (i) => setPayments((prev) => prev.filter((_, idx) => idx !== i));

  // When bill type flips, cascade to all cart lines
  useEffect(() => {
    setCart((prev) => prev.map((it) => {
      if (billType === "NonGST") return { ...it, gst_percent: 0 };
      if (it.gst_percent === 0 && it.kind !== "custom") return { ...it, gst_percent: 18 };
      return it;
    }));
  }, [billType]);

  const submitBill = async () => {
    if (Math.abs(paymentsShort) > 0.5) { toast.error(`Payments don't match total (short by ${inr(paymentsShort)})`); return; }
    try {
      const payload = {
        customer_id: customer.id || "",
        customer_name: customer.name || "Walk-in",
        customer_mobile: customer.mobile || "",
        items: cart.map(({ key, ...it }) => it),
        bill_discount: num(billDiscount),
        bill_type: billType,
        notes,
        payments: payments.filter((p) => num(p.amount) > 0),
      };
      const { data } = await api.post("/pos/bills", payload);
      toast.success(`${data.pos_no} · ${inr(data.total)} · Paid`);
      setPayOpen(false);
      setReceipt(data);
      clearCart();
      loadAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  return (
    <div className="p-6 max-w-[1600px]">
      <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-orange-600 font-semibold mb-2 flex items-center gap-2"><Storefront size={14} weight="duotone" /> Point of Sale</div>
          <h1 className="text-4xl font-bold text-slate-900" style={{ fontFamily: "var(--font-heading)" }}>POS Billing</h1>
          <p className="text-sm text-slate-600 mt-2">Retail counter · Stationery · Printing · Lamination · Services · Split payment.</p>
        </div>
        <div className="grid grid-cols-4 gap-3 min-w-[420px]" data-testid="pos-kpis">
          <MiniKpi label="Bills Today" value={kpi.bills_today} />
          <MiniKpi label="Sales Today" value={inr(kpi.sales_today)} tone="green" />
          <MiniKpi label="Cash" value={inr(kpi.cash_today)} tone="orange" />
          <MiniKpi label="UPI" value={inr(kpi.upi_today)} tone="blue" />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Left — catalog */}
        <div className="lg:col-span-3">
          <Card className="p-4 bg-white border-slate-200 mb-3">
            <div className="flex gap-2 mb-3">
              <button onClick={() => setTab("products")} data-testid="pos-tab-products"
                className={`px-4 h-9 rounded-full text-sm font-medium transition-colors ${tab === "products" ? "bg-orange-600 text-white" : "bg-white border border-slate-300 text-slate-700 hover:bg-slate-100"}`}>
                Products ({products.length})
              </button>
              <button onClick={() => setTab("services")} data-testid="pos-tab-services"
                className={`px-4 h-9 rounded-full text-sm font-medium transition-colors ${tab === "services" ? "bg-orange-600 text-white" : "bg-white border border-slate-300 text-slate-700 hover:bg-slate-100"}`}>
                Services ({services.length})
              </button>
              <div className="flex-1 relative">
                <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input data-testid="pos-search" autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, code or scan barcode…" className="pl-9 h-9" />
              </div>
              <Button variant="outline" onClick={addCustomItem} data-testid="pos-add-custom" className="h-9 gap-1 border-slate-300 whitespace-nowrap">
                <Plus size={14} /> Custom
              </Button>
            </div>
            {tab === "products" && (
              <div className="flex flex-wrap gap-1.5">
                {productCats.map((c) => (
                  <button key={c} onClick={() => setCategory(c)}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${category === c ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}>
                    {c}
                  </button>
                ))}
              </div>
            )}
          </Card>
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="pos-catalog">
            {catalog.length === 0 && <div className="col-span-full text-center text-slate-500 py-16">No items match your search.</div>}
            {catalog.map((it) => {
              const rate = tab === "products" ? num(it.rate) : (num(it.default_govt_fee) + num(it.default_service_charge));
              return (
                <button
                  key={it.id}
                  onClick={() => addToCart(it)}
                  data-testid={`pos-item-${it.id}`}
                  className="text-left p-3 rounded-lg bg-white border border-slate-200 hover:border-orange-500 hover:shadow-md transition-all"
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="text-xs font-mono text-slate-500">{it.code || (tab === "services" ? "SVC" : "")}</div>
                    <Badge variant="outline" className="text-[9px] uppercase tracking-wider bg-slate-50">{it.category || it.department || tab}</Badge>
                  </div>
                  <div className="text-sm font-medium text-slate-900 line-clamp-2">{it.name}</div>
                  <div className="mt-2 flex items-center justify-between">
                    <div className="text-orange-600 font-bold text-lg" style={{ fontFamily: "var(--font-heading)" }}>{inr(rate)}</div>
                    <Plus size={16} className="text-slate-400" />
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Right — cart */}
        <div className="lg:col-span-2">
          <Card className="p-4 bg-white border-slate-200 sticky top-4" data-testid="pos-cart">
            <div className="flex items-center gap-2 mb-3">
              <ShoppingCart size={18} weight="duotone" className="text-orange-600" />
              <div className="text-sm font-semibold" style={{ fontFamily: "var(--font-heading)" }}>Current Bill</div>
              <Badge variant="outline" className="ml-auto text-xs">{cart.length} items</Badge>
              {cart.length > 0 && <button onClick={clearCart} data-testid="pos-clear" className="text-xs text-red-600 hover:text-red-700"><Trash size={14} /></button>}
            </div>

            {/* Bill type toggle — GST invoice vs Cash Memo (non-GST) */}
            <div className="grid grid-cols-2 gap-1 mb-3 rounded-md border border-slate-200 p-1 bg-slate-50" data-testid="pos-bill-type">
              <button
                onClick={() => setBillType("GST")}
                data-testid="pos-bill-type-gst"
                className={`px-2 py-1.5 rounded text-xs font-semibold uppercase tracking-wider transition-colors ${billType === "GST" ? "bg-slate-900 text-white shadow-sm" : "text-slate-600 hover:text-slate-900"}`}
              >GST Invoice</button>
              <button
                onClick={() => setBillType("NonGST")}
                data-testid="pos-bill-type-nongst"
                className={`px-2 py-1.5 rounded text-xs font-semibold uppercase tracking-wider transition-colors ${billType === "NonGST" ? "bg-orange-600 text-white shadow-sm" : "text-slate-600 hover:text-slate-900"}`}
              >Cash Memo · Non-GST</button>
            </div>

            {/* Customer picker */}
            <div className="rounded-md border border-slate-200 bg-slate-50 p-3 mb-3">
              <div className="flex items-center gap-2 mb-2">
                <UserCircle size={18} className="text-slate-500" />
                <div className="flex-1">
                  <div className="text-sm font-medium text-slate-900" data-testid="pos-customer-name">{customer.name}</div>
                  {customer.mobile && <div className="text-[10px] text-slate-500 font-mono">{customer.mobile}</div>}
                </div>
                {customer.id && <button onClick={() => setCustomer({ id: "", name: "Walk-in", mobile: "" })} className="text-[10px] text-slate-500 hover:text-red-600 underline">clear</button>}
              </div>
              <Input value={custSearch} onChange={(e) => setCustSearch(e.target.value)} placeholder="Search customer by name / mobile / ID…" className="h-8 text-xs" data-testid="pos-cust-search" />
              {custSearch && (
                <div className="mt-2 max-h-40 overflow-y-auto space-y-1">
                  {filteredCustomers.map((c) => (
                    <button key={c.id} onClick={() => pickCustomer(c)} className="w-full text-left px-2 py-1 rounded hover:bg-white text-xs">
                      <span className="font-mono text-[10px] text-slate-500">{c.customer_no}</span> · <span className="font-medium">{c.name}</span> · {c.mobile}
                    </button>
                  ))}
                  {filteredCustomers.length === 0 && <div className="text-[10px] text-slate-500 px-2 py-1">No match — leave as Walk-in.</div>}
                </div>
              )}
            </div>

            <div className="space-y-2 max-h-[380px] overflow-y-auto pr-1 mb-3">
              {cart.length === 0 && <div className="text-center text-slate-400 text-sm py-10">Tap items on the left to add.</div>}
              {cart.map((it) => {
                const lineGross = num(it.quantity) * num(it.rate);
                const afterDisc = Math.max(0, lineGross - num(it.discount));
                return (
                  <div key={it.key} className="rounded-md border border-slate-200 p-2 bg-white" data-testid={`pos-line-${it.ref_id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">{it.name}</div>
                        <div className="text-[10px] text-slate-500">{it.code} · GST {it.gst_percent}%</div>
                      </div>
                      <button onClick={() => removeLine(it.key)} className="text-red-500 hover:text-red-700"><Trash size={13} /></button>
                    </div>
                    <div className="grid grid-cols-4 gap-1.5 mt-2 items-center">
                      <div className="col-span-2 flex items-center gap-1 border border-slate-300 rounded-md">
                        <button onClick={() => updateLine(it.key, { quantity: Math.max(0, num(it.quantity) - 1) })} className="px-2 hover:bg-slate-100"><Minus size={10} /></button>
                        <Input value={it.quantity} onChange={(e) => updateLine(it.key, { quantity: num(e.target.value) })} className="h-7 border-0 text-center text-xs px-0" />
                        <button onClick={() => updateLine(it.key, { quantity: num(it.quantity) + 1 })} className="px-2 hover:bg-slate-100"><Plus size={10} /></button>
                      </div>
                      <Input value={it.rate} onChange={(e) => updateLine(it.key, { rate: num(e.target.value) })} className="h-7 text-xs" title="Rate" />
                      <Input value={it.discount} onChange={(e) => updateLine(it.key, { discount: num(e.target.value) })} className="h-7 text-xs" placeholder="Disc" title="Discount ₹" />
                    </div>
                    <div className="flex justify-between mt-1 text-xs">
                      <span className="text-slate-500">{inr(afterDisc)}</span>
                      <span className="font-semibold">{inr(afterDisc + afterDisc * num(it.gst_percent) / 100)}</span>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="pt-3 border-t border-slate-200 space-y-1.5 text-sm">
              <Row label="Subtotal" value={inr(totals.subtotal)} />
              <div className="flex items-center gap-2">
                <span className="text-slate-600 text-xs">Bill Discount ₹</span>
                <Input value={billDiscount} onChange={(e) => setBillDiscount(num(e.target.value))} className="h-7 text-xs flex-1" data-testid="pos-bill-discount" />
              </div>
              <Row label={`GST`} value={inr(totals.gst)} />
              <div className="flex justify-between pt-2 border-t border-slate-300">
                <span className="text-sm font-semibold">Total</span>
                <span className="text-2xl font-bold text-orange-600" style={{ fontFamily: "var(--font-heading)" }} data-testid="pos-total">{inr(totals.total)}</span>
              </div>
              <Button onClick={openPayDialog} disabled={cart.length === 0} data-testid="pos-pay-btn" className="w-full h-11 mt-2 bg-orange-600 hover:bg-orange-700 text-white gap-2 text-sm">
                <CurrencyInr size={16} weight="bold" /> Take Payment
              </Button>
            </div>
          </Card>
        </div>
      </div>

      {/* Payment dialog */}
      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent className="max-w-lg bg-white">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "var(--font-heading)" }}>Take Payment</DialogTitle>
            <DialogDescription className="sr-only">Record one or more payment methods. Split allowed.</DialogDescription>
          </DialogHeader>
          <div className="py-2 space-y-3">
            <div className="rounded-md bg-slate-50 border border-slate-200 p-3 flex items-center justify-between">
              <span className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Bill Total</span>
              <span className="text-2xl font-bold text-orange-600" style={{ fontFamily: "var(--font-heading)" }}>{inr(totals.total)}</span>
            </div>
            <div className="space-y-2">
              {payments.map((p, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-end" data-testid={`pos-pay-line-${i}`}>
                  <div className="col-span-4">
                    <Label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">Method</Label>
                    <Select value={p.method} onValueChange={(v) => updatePayment(i, { method: v })}>
                      <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>{PAY_METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-4">
                    <Label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">Amount ₹</Label>
                    <Input value={p.amount} onChange={(e) => updatePayment(i, { amount: num(e.target.value) })} className="h-9" data-testid={`pos-pay-amount-${i}`} />
                  </div>
                  <div className="col-span-3">
                    {p.method !== "Cash" && (
                      <>
                        <Label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">UTR/Ref</Label>
                        <Input value={p.txn_ref} onChange={(e) => updatePayment(i, { txn_ref: e.target.value })} className="h-9 text-xs" />
                      </>
                    )}
                  </div>
                  <div className="col-span-1">
                    {payments.length > 1 && <button onClick={() => removePayment(i)} className="text-red-500 hover:text-red-700 h-9"><Trash size={14} /></button>}
                  </div>
                </div>
              ))}
              {payments.length < 3 && (
                <button onClick={addSplit} data-testid="pos-split-add" className="text-xs text-orange-600 hover:text-orange-700 underline underline-offset-2">
                  + Split payment (add another method)
                </button>
              )}
            </div>
            <div className="pt-2 border-t border-slate-200 text-xs flex justify-between">
              <span className="text-slate-600">Payments total</span>
              <span className={`font-semibold ${Math.abs(paymentsShort) < 0.5 ? "text-emerald-700" : "text-red-600"}`} data-testid="pos-pay-status">
                {inr(paymentsTotal)} {Math.abs(paymentsShort) < 0.5 ? "✓" : `(short ${inr(paymentsShort)})`}
              </span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayOpen(false)}>Cancel</Button>
            <Button onClick={submitBill} data-testid="pos-confirm-bill" className="bg-orange-600 hover:bg-orange-700 text-white gap-2"><CheckCircle size={14} weight="fill" /> Confirm & Print</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Receipt dialog */}
      <Dialog open={!!receipt} onOpenChange={(v) => !v && setReceipt(null)}>
        <DialogContent className="max-w-sm bg-white">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "var(--font-heading)" }}>Receipt · {receipt?.pos_no}</DialogTitle>
            <DialogDescription className="sr-only">Printable POS receipt suitable for 58/80 mm thermal printer.</DialogDescription>
          </DialogHeader>
          {receipt && (
            <div className="print-area font-mono text-[11px] leading-relaxed" data-testid="pos-receipt">
              <div className="text-center border-b border-dashed pb-2 mb-2">
                <div className="font-bold text-sm" style={{ fontFamily: "var(--font-heading)" }}>PRIYANKA ENTERPRISE ERP</div>
                <div className="uppercase tracking-widest">{receipt.bill_type === "NonGST" ? "Cash Memo · Retail" : "GST Invoice · Retail"}</div>
              </div>
              <div className="text-[10px] mb-1">
                <div>#{receipt.pos_no} · {new Date(receipt.created_at).toLocaleString("en-IN")}</div>
                <div>Cust: {receipt.customer_name} {receipt.customer_mobile ? `· ${receipt.customer_mobile}` : ""}</div>
                <div>Op: {receipt.created_by}</div>
              </div>
              <div className="border-t border-dashed my-1" />
              {(receipt.items || []).map((it, i) => (
                <div key={i} className="mb-1">
                  <div className="flex justify-between">
                    <span className="truncate mr-2">{it.name}</span>
                    <span>{inr(it.line_total || 0)}</span>
                  </div>
                  <div className="text-[9px] text-slate-500">{num(it.quantity)} × {inr(it.rate)}{num(it.discount) ? ` − ${inr(it.discount)}` : ""}{receipt.bill_type !== "NonGST" && num(it.gst_percent) ? ` +GST ${it.gst_percent}%` : ""}</div>
                </div>
              ))}
              <div className="border-t border-dashed my-1" />
              <div className="flex justify-between"><span>Subtotal</span><span>{inr(receipt.subtotal)}</span></div>
              {num(receipt.bill_discount) > 0 && <div className="flex justify-between"><span>Bill Disc</span><span>− {inr(receipt.bill_discount)}</span></div>}
              {receipt.bill_type !== "NonGST" && <div className="flex justify-between"><span>GST</span><span>{inr(receipt.gst_amount)}</span></div>}
              <div className="border-t border-dashed my-1" />
              <div className="flex justify-between font-bold text-sm"><span>TOTAL</span><span>{inr(receipt.total)}</span></div>
              <div className="border-t border-dashed my-1" />
              {(receipt.payments || []).map((p, i) => (
                <div key={i} className="flex justify-between text-[10px]">
                  <span>{p.method}{p.txn_ref ? ` · ${p.txn_ref}` : ""}</span>
                  <span>{inr(p.amount)}</span>
                </div>
              ))}
              {receipt.bill_type === "NonGST" && <div className="text-[9px] italic text-slate-500 mt-1">Non-GST cash memo — no GST charged.</div>}
              <div className="text-center text-[9px] mt-2 pt-2 border-t border-dashed">Thank you · Visit again</div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setReceipt(null)}>Close</Button>
            {receipt?.customer_mobile && (
              <a
                href={`https://wa.me/91${(receipt.customer_mobile || "").replace(/\D/g, "").slice(-10)}?text=${encodeURIComponent(`Namaste, thank you for your visit. POS Bill: ${receipt.pos_no}, Total ₹${receipt.total}. — DigitalSetu ERP`)}`}
                target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-2 h-9 px-3 rounded-md border border-emerald-300 text-emerald-700 hover:bg-emerald-50 text-sm"
              ><WhatsappLogo size={14} weight="fill" /> WhatsApp</a>
            )}
            <Button onClick={() => window.print()} data-testid="pos-receipt-print" className="bg-slate-900 hover:bg-slate-800 text-white gap-2"><Printer size={14} /> Print</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MiniKpi({ label, value, tone }) {
  const t = { green: "text-emerald-700", orange: "text-orange-600", blue: "text-blue-700" }[tone] || "text-slate-900";
  return (
    <div className="p-3 rounded-md border border-slate-200 bg-white">
      <div className="text-[9px] uppercase tracking-[0.15em] font-semibold text-slate-500">{label}</div>
      <div className={`text-xl font-bold mt-0.5 ${t}`} style={{ fontFamily: "var(--font-heading)" }}>{value}</div>
    </div>
  );
}
function Row({ label, value }) {
  return <div className="flex justify-between text-sm py-0.5"><span className="text-slate-600">{label}</span><span className="font-medium">{value}</span></div>;
}
